import React, { useState } from 'react';
import { Table, Badge, Button, Pagination, Dropdown, ButtonGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaSort, FaSortUp, FaSortDown, FaEye, FaCheck, FaTimes, FaEllipsisV } from 'react-icons/fa';
import dealService, { Deal } from '../../services/dealService';

interface DealTableProps {
  deals: Deal[];
  currentSort: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onDealUpdate?: () => void; // Callback to refresh deals after status update
  // Optional override for the "view deal" click handler. When provided,
  // DealTable calls this instead of navigating to /investor-deals/:id.
  // Used by the Agent Portal Docs Hub to keep deal drill-down inside
  // the HubShell chrome (so Dashboard/Sign Out/tab bar stay visible)
  // without duplicating DealDashboard or forking the route tree.
  onDealClick?: (dealId: number) => void;
}

const DealTable: React.FC<DealTableProps> = ({
  deals,
  currentSort,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onDealUpdate,
  onDealClick,
}) => {
  const navigate = useNavigate();
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  const getSortIcon = (field: string) => {
    if (currentSort !== field) return <FaSort className="text-muted" />;
    return sortOrder === 'desc' ? <FaSortDown /> : <FaSortUp />;
  };

  const handleViewDeal = (dealId: number) => {
    // Prefer the parent-provided handler (hub tab context). Falls back to
    // navigating to the standalone /investor-deals/:id route for the
    // original non-hub usage.
    if (onDealClick) {
      onDealClick(dealId);
    } else {
      navigate(`/investor-deals/${dealId}`);
    }
  };

  const handleQuickStatusUpdate = async (dealId: number, status: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setUpdatingStatus(dealId);
      await dealService.updateDealStatus(dealId, status);
      
      // Refresh the deals list
      if (onDealUpdate) {
        onDealUpdate();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      // Could add toast notification here
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      new: 'primary',
      reviewing: 'info',
      pursuing: 'warning',
      offered: 'secondary',
      won: 'success',
      passed: 'danger',
      expired: 'dark'
    };
    
    const labels: Record<string, string> = {
      new: '🆕 New',
      reviewing: '📋 Reviewing',
      pursuing: '🎯 Pursuing',
      offered: '💼 Offered',
      won: '✅ Won',
      passed: '❌ Passed',
      expired: '⏰ Expired'
    };

    return (
      <Badge bg={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const renderPagination = () => {
    const items = [];
    const maxPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(totalPages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => onPageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-1" />);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => onPageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-2" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => onPageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mb-0">
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        />
        {items}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        />
      </Pagination>
    );
  };

  return (
    <>
      <div className="table-responsive">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th 
                onClick={() => onSort('flip_grade')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Grade {getSortIcon('flip_grade')}
              </th>
              <th>Address</th>
              <th 
                onClick={() => onSort('list_price')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Price {getSortIcon('list_price')}
              </th>
              <th>ARV</th>
              <th 
                onClick={() => onSort('flip_roi')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                ROI {getSortIcon('flip_roi')}
              </th>
              <th 
                onClick={() => onSort('flip_profit')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Profit {getSortIcon('flip_profit')}
              </th>
              <th>MAO</th>
              <th 
                onClick={() => onSort('scan_date')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Date {getSortIcon('scan_date')}
              </th>
              <th>Status</th>
              <th className="text-center" style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deals.map((deal) => (
              <tr 
                key={deal.id}
                style={{ cursor: 'pointer' }}
                onClick={() => handleViewDeal(deal.id)}
                className="clickable-row"
              >
                <td>
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle me-2"
                      style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: dealService.getGradeColor(deal.flip_grade)
                      }}
                    />
                    <span className="fw-bold">
                      {deal.flip_grade} {dealService.getGradeEmoji(deal.flip_grade)}
                    </span>
                  </div>
                </td>
                <td>
                  <div>
                    <div className="fw-medium">{deal.unparsed_address || deal.address || `${deal.city}, AZ`}</div>
                    <small className="text-muted">
                      {deal.bedrooms}bd/{deal.bathrooms}ba • {deal.living_area.toLocaleString()}sf • MLS #{deal.listing_id}
                    </small>
                  </div>
                </td>
                <td className="text-nowrap">
                  {dealService.formatCurrency(deal.list_price)}
                </td>
                <td className="text-nowrap">
                  {dealService.formatCurrency(deal.renovated_arv)}
                </td>
                <td className="text-nowrap">
                  <span className={deal.flip_roi >= 20 ? 'text-success fw-bold' : ''}>
                    {dealService.formatROI(deal.flip_roi)}
                  </span>
                </td>
                <td className="text-nowrap">
                  <span className="text-success">
                    {dealService.formatCurrency(deal.flip_profit)}
                  </span>
                </td>
                <td className="text-nowrap">
                  {dealService.formatCurrency(deal.mao_20pct)}
                </td>
                <td className="text-nowrap">
                  <small>
                    {deal.scan_date 
                      ? new Date(deal.scan_date).toLocaleDateString()
                      : 'N/A'
                    }
                  </small>
                </td>
                <td>
                  {getStatusBadge(deal.deal_status)}
                  {(deal as any).mls_status && (
                    <Badge bg={
                      (deal as any).mls_status === 'Active' ? 'success' :
                      (deal as any).mls_status === 'Pending' ? 'warning' : 'secondary'
                    } className="ms-1" style={{ fontSize: '9px' }}>
                      MLS: {(deal as any).mls_status}
                    </Badge>
                  )}
                </td>
                <td className="text-center">
                  <ButtonGroup size="sm">
                    {/* Quick Status Buttons - Only show if status is 'new' or 'reviewing' */}
                    {(deal.deal_status === 'new' || deal.deal_status === 'reviewing') && (
                      <>
                        <Button
                          variant="outline-success"
                          onClick={(e) => handleQuickStatusUpdate(deal.id, 'reviewing', e)}
                          disabled={updatingStatus === deal.id}
                          title="Mark as Reviewing"
                          style={{ fontSize: '12px', padding: '2px 6px' }}
                        >
                          <FaCheck /> Review
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={(e) => handleQuickStatusUpdate(deal.id, 'passed', e)}
                          disabled={updatingStatus === deal.id}
                          title="Pass on Deal"
                          style={{ fontSize: '12px', padding: '2px 6px' }}
                        >
                          <FaTimes /> Pass
                        </Button>
                      </>
                    )}
                    
                    {/* View Button */}
                    <Button
                      variant="outline-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDeal(deal.id);
                      }}
                      title="View Details"
                      style={{ fontSize: '12px', padding: '2px 8px' }}
                    >
                      <FaEye />
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center p-3 border-top">
          <div className="text-muted">
            Showing {deals.length} of {(currentPage - 1) * 25 + deals.length} deals
          </div>
          {renderPagination()}
        </div>
      )}
    </>
  );
};

export default DealTable;